import type { ReactNode } from 'react';
import { IconX } from '@tabler/icons-react';
import { IconButton, ReferenceChip, WP_ICON_NAV } from '@wapharma/design-system';
import { getEntityHref, getEntityLabel, getEntityTypeName } from '../lib/entityRegistry';
import { EntityToneWell, EntityTypeMark } from './EntityTypeMark';
import styles from './ComposerReferenceChip.module.css';

type EntityReferenceChipProps = {
  entityType: string;
  entityId: string;
  title?: string | null;
  subtitle?: string | null;
  imageUrl?: string | null;
  href?: string | null;
  /** When set, show remove control (composer draft). */
  onRemove?: () => void;
  disabled?: boolean;
};

/** Reference chip with mandatory type name + toned glyph. */
export function EntityReferenceChip({
  entityType,
  entityId,
  title,
  subtitle,
  imageUrl,
  href,
  onRemove,
  disabled = false,
}: EntityReferenceChipProps) {
  const chip = (
    <ReferenceChip
      typeLabel={getEntityTypeName(entityType)}
      typeSlot={<EntityTypeMark entityType={entityType} />}
      title={title?.trim() || getEntityLabel(entityType, entityId)}
      subtitle={subtitle}
      href={href ?? getEntityHref(entityType, entityId)}
      imageUrl={imageUrl}
      leading={imageUrl ? undefined : <EntityToneWell entityType={entityType} />}
    />
  );

  if (!onRemove) return chip;

  return (
    <div className={styles.wrap}>
      {chip}
      <IconButton
        type="button"
        className={styles.remove}
        variant="ghost"
        size="sm"
        aria-label="Retirer la référence"
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
      >
        <IconX {...WP_ICON_NAV} />
      </IconButton>
    </div>
  );
}

/** @deprecated prefer EntityReferenceChip — kept for non-entity chips. */
export function ComposerReferenceChip({
  typeLabel,
  title,
  subtitle,
  href,
  imageUrl,
  icon,
  typeSlot,
  leading,
  onRemove,
  disabled = false,
}: {
  typeLabel: string;
  title: string;
  subtitle?: string | null;
  href?: string | null;
  imageUrl?: string | null;
  icon?: ReactNode;
  typeSlot?: ReactNode;
  leading?: ReactNode;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <div className={styles.wrap}>
      <ReferenceChip
        typeLabel={typeLabel}
        title={title}
        subtitle={subtitle}
        href={href}
        imageUrl={imageUrl}
        icon={icon}
        typeSlot={typeSlot}
        leading={leading}
      />
      <IconButton
        type="button"
        className={styles.remove}
        variant="ghost"
        size="sm"
        aria-label="Retirer la référence"
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
      >
        <IconX {...WP_ICON_NAV} />
      </IconButton>
    </div>
  );
}
